/* eslint-disable max-nested-callbacks */
/* eslint-disable consistent-return */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import CatchmentDto from 'catchment/dto/CatchmentDto'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getLabel } from 'utils/StoreUtils'
import { calculateAverage, REMARK_CODES, searchMaxAnalysis, searchMaxResult, searchMinAnalysis, searchP90Analysis, searchP90Result } from 'utils/AnalyseUtils'
import useApplicationSetting, { listStringParser } from 'utils/customHook/useApplicationSetting'
import i18n from 'simple-react-i18n'
import { groupBy, isUndefined, keyBy, max, maxBy, mean, round, sumBy, uniqBy, keys, compact, omit, countBy, range, meanBy } from 'lodash'
import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'
import { substringText } from 'utils/StringUtil'
import { push } from '@lagunovsky/redux-react-router'
import DtoAnalysisUltraLight from 'quality/dto/analyse/DtoAnalysisUltraLight'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import { THRESHOLD_COLOR, THRESHOLD_DISCRIMINANT, THRESHOLD_DISPLAY, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'
import { THRESHOLD } from 'quality/constants/QualityConstants'
import { CardTable } from 'components/datatable/NewTable'
import { darkBlue, whiteColor } from 'utils/constants/ColorTheme'
import { NITRATES_CODE, DEFAULT_THRESHOLD_NITRATES, DEFAULT_THRESHOLD_PESTICIDES_1, DEFAULT_THRESHOLD_PESTICIDES_2, AVERAGE_SUBSTANCE, MAX_SUBSTANCE, AVERAGE_SUM, MAX_SUM } from 'catchment/constants/CatchmentConstant'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_SANDRE_CODE, SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import { getLastYearWithAnalysis, getNitratesState, getNitratesThresholdColor, getPesticidesState, getPesticidesThresholdColor, getSpecificThresholdColor, isExceedAverageSubstance, isExceedAverageSumSubstance, isExceedMaxSubstance, isExceedMaxSumSubstance, isExceedNitrates, isExceedPesticides } from 'catchment/utils/CatchmentUtil'
import ExportFileModal from 'components/modal/ExportFileModal'
import useBoolean from 'utils/customHook/useBoolean'
import { formatData } from 'utils/ExportDataUtil'
import ExportAction from 'export/actions/ExportAction'
import { getYear } from 'date-fns'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import { getEndOfTheYear } from 'utils/DateUtil'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'

const TooltipNitrates = ({
    analyse = {},
    threshold1,
    showCode = true,
}) => (
    <TooltipAnalyse
        title={`${i18n.nitrates} ${showCode ? `[${NITRATES_CODE}]` : ''}`}
        analyse={analyse}
        threshold={{
            parameterCode: analyse.parameter,
            threshold1,
            thresholdType: THRESHOLD_TYPE.INCREASING,
            display: THRESHOLD_DISPLAY.INCREASING,
            discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
        }}
        overrideColors={[THRESHOLD_COLOR.green, THRESHOLD_COLOR.red]}
    />
)

TooltipNitrates.propTypes = {
    analyse: PropTypes.instanceOf(DtoAnalysisUltraLight),
    threshold1: PropTypes.number,
    showCode: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
}

const ContaminationTableSummaryNitrates = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    useP90 = false,
    startPeriod = 0,
}) => {
    const dispatch = useDispatch()

    const {
        cities,
        qualitometers,
        departments,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        qualitometers: store.QualityReducer.qualitometersLight,
        departments: store.CityReducer.departments,
    }), shallowEqual)

    const thresholdNitrates = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_NITRATES)
    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)
    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const {
        value: isExportModalOpen,
        toggle: toggleExportModal,
    } = useBoolean(false)

    const nitratesAnalysisGroup = useMemo(() => {
        const nitratesAnalysis = analysis.filter(({ parameter }) => parameter === NITRATES_CODE)
        return groupBy(nitratesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const rangeDate = range(getYear(startPeriod), getYear(useEndPeriod(getEndOfTheYear)) + 1, 1)

    const data = useMemo(() => {
        return catchmentPoints.map(point => {
            const catchment = catchments.find(c => c.id === point.id)
            const qualito = qualitometers.find(q => q.code === point.codeWithoutDesignation)

            const nitratesAnalysis = nitratesAnalysisGroup[qualito?.id] || []
            const minNitrates = searchMinAnalysis(nitratesAnalysis)
            const averageNitrates = calculateAverage(nitratesAnalysis)
            const maxNitrates = searchMaxAnalysis(nitratesAnalysis)
            const p90Nitrates = searchP90Analysis(nitratesAnalysis)

            const nitrates = qualito?.id && nitratesAnalysisGroup[qualito.id] || []
            const nitratesGroupYear = groupBy(nitrates, a => getYear(a.sampleDate))
            const nitratesData = rangeDate.reduce((acc, year) => {
                acc[`${useP90 ? 'P90' : 'Max'} NO3 ${year}`] = {
                    value: useP90 ? searchP90Result(nitratesGroupYear[year]) : searchMaxResult(nitratesGroupYear[year]),
                    format: '0.00000',
                    cellType: 'number',
                }
                return acc
            }, {})

            const { above: nbNitratesAbove } = countBy(nitrates, a => a.result > thresholdNitrates ? 'above' : 'below')

            const nitratesGroupOperation = groupBy(nitrates, 'operation')
            const nbNitratesOperation = keys(nitratesGroupOperation).length
            const nbExceedNitrates = keys(nitratesGroupOperation).filter(opeId => {
                const {
                    [opeId]: nitratesByOperation = [],
                } = nitratesGroupOperation
                return isExceedNitrates(nitratesByOperation, thresholdNitrates, useP90)
            }).length

            return {
                id: catchment?.id,
                code: { value: catchment?.code },
                name: { value: catchment?.name },
                stationCode: { value: point.code },
                mainPointLabel: { value: point.mainPoint ? i18n.yes : i18n.no },
                city: { value: qualito?.townCode && getLabel(cities, qualito.townCode, showInseeCode ? 'labelWithCode' : 'name') },
                nbAnalysisNitrates: { value: nitratesAnalysis.length, align: 'right' },
                min: {
                    value: minNitrates?.value,
                    align: 'right',
                    color: getNitratesThresholdColor(minNitrates?.result, thresholdNitrates),
                    tooltip: isUndefined(minNitrates) ? undefined : (<TooltipNitrates analyse={minNitrates} threshold1={thresholdNitrates} showCode={showSandreCode}/>),
                },
                averageShort: {
                    value: averageNitrates,
                    align: 'right',
                    color: getNitratesThresholdColor(averageNitrates, thresholdNitrates),
                    tooltip: isUndefined(averageNitrates) ? undefined : (<TooltipNitrates analyse={{ value: averageNitrates }} threshold1={thresholdNitrates} showCode={showSandreCode}/>),
                },
                max: {
                    value: maxNitrates?.value,
                    align: 'right',
                    color: getNitratesThresholdColor(maxNitrates?.result, thresholdNitrates),
                    tooltip: isUndefined(maxNitrates) ? undefined : (<TooltipNitrates analyse={maxNitrates} threshold1={thresholdNitrates} showCode={showSandreCode}/>),
                },
                percentile90: {
                    value: p90Nitrates?.value,
                    align: 'right',
                    color: getNitratesThresholdColor(p90Nitrates?.result, thresholdNitrates),
                    tooltip: isUndefined(p90Nitrates) ? undefined : (<TooltipNitrates analyse={p90Nitrates} threshold1={thresholdNitrates} showCode={showSandreCode}/>),
                },
                departmentNumber: catchment?.department,
                department: departments.find(d => d.code === catchment?.department)?.name,
                nitratesData,
                state: i18n[getNitratesState(nitrates, { thresholdNitrates, useP90 })],
                overrunNb: nbNitratesAbove,
                nitratesNonConformityPercentage: nitrates.length && nbNitratesOperation ? (nbExceedNitrates / nbNitratesOperation) * 100 : undefined,
            }
        })
    }, [catchmentPoints, catchments, cities, nitratesAnalysisGroup, qualitometers, thresholdNitrates, showSandreCode, showInseeCode, departments, useP90, rangeDate])

    const exportQualityReportSummaryNitrates = (type) => {
        const dataTable = catchmentPoints.map(point => {
            const dataLinked = data.find(p => p.stationCode.value === point.code)
            const qualito = qualitometers.find(q => q.code === point?.codeWithoutDesignation)

            const allData = {
                catchment: { value: dataLinked.code.value, cellType: 'string' },
                label: dataLinked.name.value,
                mainPointLabel: point.mainPoint ? i18n.yes : i18n.no,
                bsscode: { value: qualito?.code, cellType: 'string' },
                name: qualito?.name,
                departmentNumber: dataLinked.departmentNumber,
                department: dataLinked.department,
                city: dataLinked.city.value,
                state: dataLinked.state,
                ...dataLinked.nitratesData,
                nbAnalysisNitrates: { value: dataLinked.nbAnalysisNitrates.value, format: '0', cellType: 'number' },
                percentile90: { value: dataLinked.percentile90.value, format: '0.00000' },
                min: { value: dataLinked.min.value, format: '0.00000' },
                average: { value: dataLinked.averageShort.value, format: '0.00000' },
                max: { value: dataLinked.max.value, format: '0.00000' },
                overrunNb: { value: dataLinked.overrunNb, cellType: 'number' },
            }
            return showInseeCode ? allData : omit(allData, 'departmentNumber')
        })
        const dataWithHeaders = dataTable.length ? [
            { ...dataTable[0], headers: keys(dataTable[0]) },
            ...dataTable.slice(1),
        ] : []
        dispatch(ExportAction.export(formatData(dataWithHeaders), type, i18n.qualityReportSummary))
    }

    const exportEvolutionContaminationNitrates = (type) => {
        const dataContamination = catchmentPoints.map(point => {
            const dataLinked = data.find(p => p.stationCode.value === point.code)
            const qualitometer = qualitometers.find(q => q.code === point.codeWithoutDesignation)

            const allData = {
                code: dataLinked.code.value,
                name: dataLinked.name.value,
                departmentNumber: dataLinked.departmentNumber,
                department: dataLinked.department,
                x: { value: qualitometer?.x, cellType: 'number' },
                y: { value: qualitometer?.y, cellType: 'number' },
                projection: { value: qualitometer?.projection, cellType: 'number' },
                mainPointLabel: point.mainPoint ? i18n.yes : i18n.no,
                state: dataLinked.state,
                nitratesThreshold: { value: thresholdNitrates, cellType: 'number' },
                percentile90: { value: dataLinked.percentile90.value, format: '0.00000' },
                min: { value: dataLinked.min.value, format: '0.00000' },
                average: { value: dataLinked.averageShort.value, format: '0.00000' },
                max: { value: dataLinked.max.value, format: '0.00000' },
                nitratesNonConformityPercentage: { value: dataLinked.nitratesNonConformityPercentage, format: '0.00', cellType: 'number' },
            }
            return showInseeCode ? allData : omit(allData, ['departmentNumber'])
        })
        const dataWithHeaders = dataContamination.length ? [
            { ...dataContamination[0], headers: keys(dataContamination[0]) },
            ...dataContamination.slice(1),
        ] : []
        dispatch(ExportAction.export(formatData(dataWithHeaders), type, i18n.evolutionContaminationNitratesPesticides))
    }

    return (
        <>
            <CardTable
                title={i18n.catchmentsQualitySummary}
                rows={data}
                headers={[
                    'code',
                    'name',
                    { key: 'stationCode', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.stationCode}</span>) },
                    { key: 'mainPointLabel', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.mainPointLabel}</span>) },
                    'city',
                    { key: 'nbAnalysisNitrates', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.nbAnalysisNitrates}</span>) },
                    'min',
                    'averageShort',
                    'max',
                    'percentile90',
                ]}
                onClickRow={s => dispatch(push(`/station/catchment/${s.id}`))}
                rowsPerPageOptions={nbPerPageLabel}
                color={darkBlue}
                hideNbElements
                actions={[{
                    icon: 'file_download',
                    tooltip: i18n.export,
                    color: whiteColor,
                    onClick: toggleExportModal,
                }]}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={() => toggleExportModal()}
                data={[{
                    name: `${i18n.qualityReportSummary} (${i18n.nitrates})`,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => exportQualityReportSummaryNitrates('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => exportQualityReportSummaryNitrates('csv'),
                    }],
                }, {
                    name: `${i18n.evolutionOfContaminations} (${i18n.nitrates})`,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => exportEvolutionContaminationNitrates('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => exportEvolutionContaminationNitrates('csv'),
                    }],
                }]}
            />
        </>
    )
}

ContaminationTableSummaryNitrates.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
    startPeriod: PropTypes.number,
}

const ContaminationTableSummaryPesticides = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
    startPeriod = 0,
}) => {
    const dispatch = useDispatch()

    const {
        parameters,
        cities,
        qualityThresholds,
        qualitometers,
        departments,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        cities: store.CityReducer.cities,
        qualityThresholds: store.QualityReducer.qualityThresholds,
        qualitometers: store.QualityReducer.qualitometersLight,
        departments: store.CityReducer.departments,
    }), shallowEqual)

    const thresholdPesticides1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_1)
    const thresholdPesticides2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_2)
    const specificThreshold = useApplicationSetting('CATCHMENT_SPECIFIC_THRESHOLD')
    const listSumPesticides = useApplicationSetting('CATCHMENT_LIST_SUM_PESTICIDES', listStringParser)
    const calculationMethod = useApplicationSetting('CATCHMENT_OVERVIEW_CALCULATION_METHOD', listStringParser)
    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)
    const endPeriod = useEndPeriod(getEndOfTheYear)

    const {
        value: isExportModalOpen,
        toggle: toggleExportModal,
    } = useBoolean(false)

    const threshold = useMemo(() => qualityThresholds.find(t => t.thresholdCode === specificThreshold && t.thresholdType === THRESHOLD.PC), [qualityThresholds, specificThreshold])
    const thresholdsIndexed = useMemo(() => keyBy(threshold?.thresholds ?? [], 'parameterCode'), [threshold?.thresholds])

    const optionsCalculState = useMemo(() => ({
        thresholdPesticides1,
        thresholdPesticides2,
        threshold,
        listSumPesticides,
        calculationMethod,
    }), [thresholdPesticides1, thresholdPesticides2, threshold, listSumPesticides, calculationMethod])

    const pesticidesAnalysisGroup = useMemo(() => {
        const pesticidesAnalysis = analysis.filter(({ parameter }) => parameter !== NITRATES_CODE)
        return groupBy(pesticidesAnalysis, STATION_TYPE_NAME.qualitometer)
    }, [analysis])

    const rangeDate = range(getYear(startPeriod), getYear(endPeriod) + 1, 1)

    const data = useMemo(() => {
        return catchmentPoints.map(point => {
            const catchment = catchments.find(c => c.id === point.id)
            const qualito = qualitometers.find(q => q.code === point.codeWithoutDesignation)

            const pesticidesAnalysis = pesticidesAnalysisGroup[qualito?.id] || []
            const quatifiedPesticidesAnalysis = pesticidesAnalysis.filter(a => a.remark === REMARK_CODES.VALID)
            const listSumPesticideAnalysis = listSumPesticides.length ? quatifiedPesticidesAnalysis.filter(a => listSumPesticides.includes(a.parameter)) : quatifiedPesticidesAnalysis

            const paramGroup = groupBy(quatifiedPesticidesAnalysis, a => a.parameter)

            const allAveragePesticides = keys(paramGroup).map(key => ({ parameter: key, result: sumBy(paramGroup[key], 'result') / uniqBy(paramGroup[key], a => `${a.qualitometer}#${a.operation}`).length }))
            const averagePesticide = maxBy(allAveragePesticides, 'result')?.result

            const allMaxPesticides = keys(paramGroup).map(key => ({ parameter: key, result: maxBy(paramGroup[key], 'result')?.result }))
            const maxPesticide = maxBy(quatifiedPesticidesAnalysis, 'result')?.result

            const groupOperation = groupBy(listSumPesticideAnalysis, 'operation')
            const allSumPesticide = keys(groupOperation).map(op => sumBy(groupOperation[op], 'result'))
            const maxSumPesticide = max(allSumPesticide)
            const averageSumPesticides = allSumPesticide.length > 0 ? mean(allSumPesticide) : undefined

            const { paramAverage, paramMax } = keys(paramGroup).reduce((acc, param) => {
                const th = thresholdsIndexed[param]?.threshold1 ?? thresholdPesticides1
                const maxParam = maxBy(paramGroup[param], 'result')?.result
                // const averageParam = meanBy(paramGroup[param], 'result')
                const averageParam = sumBy(paramGroup[param], 'result') / uniqBy(paramGroup[param], a => `${a.qualitometer}#${a.operation}`).length
                const label = getLabel(parameters, param, 'name')
                return {
                    paramAverage: compact([...acc.paramAverage, averageParam > th && `${label} (${round(averageParam, 4)})`]),
                    paramMax: compact([...acc.paramMax, maxParam > th && `${label} (${round(maxParam, 4)})`]),
                }
            }, { paramAverage: [], paramMax: [] })

            return {
                id: catchment.id,
                code: { value: catchment.code },
                name: { value: catchment.name },
                stationCode: { value: point.code },
                mainPointLabel: { value: point.mainPoint ? i18n.yes : i18n.no },
                city: { value: qualito?.townCode && getLabel(cities, qualito.townCode, showInseeCode ? 'labelWithCode' : 'name') },
                nbAnalysisPesticides: { value: pesticidesAnalysis.length, align: 'right' },
                nbQuantifications: { value: quatifiedPesticidesAnalysis.length, align: 'right' },
                pesticidesAverageMax: {
                    value: isUndefined(averagePesticide) ? undefined : round(averagePesticide, 4),
                    align: 'right',
                    color: getSpecificThresholdColor(allAveragePesticides, thresholdPesticides1, thresholdPesticides2, thresholdsIndexed),
                },
                paramConcernedByAnnualAverageAboveThreshold: {
                    value: substringText(paramAverage.join(', ')),
                    tooltip: paramAverage.length ? (<>{paramAverage.map((p, i) => (<React.Fragment key={i}>{p}<br/></React.Fragment>))}</>) : undefined,
                },
                maxMaxSubstance: {
                    value: isUndefined(maxPesticide) ? undefined : round(maxPesticide, 4),
                    align: 'right',
                    color: getSpecificThresholdColor(allMaxPesticides, thresholdPesticides1, thresholdPesticides2, thresholdsIndexed),
                },
                paramConcernedByMaxAboveThreshold: {
                    value: substringText(paramMax.join(', ')),
                    tooltip: paramMax.length ? (<>{paramMax.map((p, i) => (<React.Fragment key={i}>{p}<br/></React.Fragment>))}</>) : undefined,
                },
                maxSumPesticides: {
                    value: isUndefined(maxSumPesticide) ? undefined : round(maxSumPesticide, 4),
                    align: 'right',
                    color: getPesticidesThresholdColor(maxSumPesticide, thresholdPesticides1, thresholdPesticides2),
                },
                averageSumPesticides: {
                    value: isUndefined(averageSumPesticides) ? undefined : round(averageSumPesticides, 4),
                    align: 'right',
                    color: getPesticidesThresholdColor(averageSumPesticides, thresholdPesticides1, thresholdPesticides2),
                },
            }
        })
    }, [catchmentPoints, catchments, cities, parameters, pesticidesAnalysisGroup, qualitometers, thresholdPesticides1, thresholdPesticides2, listSumPesticides, showInseeCode, thresholdsIndexed])

    const exportEvolutionContaminationPesticides = (type) => {
        const dataContamination = catchmentPoints.map(point => {
            const catchment = catchments.find(c => c.id === point.id)
            const qualitometer = qualitometers.find(q => q.code === point.codeWithoutDesignation)

            const pesticides = qualitometer?.id && (pesticidesAnalysisGroup[qualitometer.id])?.filter(p => p.remark === REMARK_CODES.VALID) || []
            const pesticidesYearGroup = groupBy(pesticides, a => getYear(a.sampleDate))

            const sumPesticides = listSumPesticides.length ? pesticides.filter(a => listSumPesticides.includes(a.parameter)) : pesticides
            const sumPesticidesYearGroup = groupBy(sumPesticides, a => getYear(a.sampleDate))

            const averageTotal = mean(keys(pesticidesYearGroup).map(year => {
                const listSum = listSumPesticides.length ? pesticidesYearGroup[year].filter(a => listSumPesticides.includes(a.parameter)) : pesticidesYearGroup[year]
                const nbOperation = uniqBy(pesticidesYearGroup[year], a => `${a.qualitometer}#${a.operation}`).length
                return sumBy(listSum, 'result') / nbOperation
            }))

            const paramGroup = groupBy(pesticides, a => a.parameter)
            const averageMax = max(keys(paramGroup).map(param => {
                const paramYearGroup = groupBy(paramGroup[param], a => getYear(a.sampleDate))
                return meanBy(keys(paramYearGroup), y => sumBy(paramYearGroup[y], 'result') / uniqBy(paramYearGroup[y], a => `${a.qualitometer}#${a.operation}`).length)
            }))

            const annualAverageMax = max(keys(sumPesticidesYearGroup).map(year => sumBy(sumPesticidesYearGroup[year], 'result') / uniqBy(sumPesticidesYearGroup[year], a => `${a.qualitometer}#${a.operation}`).length))

            const sumGroupByOperation = groupBy(sumPesticides, 'operation')
            const maxSumPesticides = max(keys(sumGroupByOperation).map(key => sumBy(sumGroupByOperation[key], 'result')))

            const pesticidesGroupOperation = groupBy(pesticides, 'operation')
            const nbPesticidesOperation = keys(pesticidesGroupOperation).length
            const nbExceedSumPesticides = keys(pesticidesGroupOperation).filter(opeId => {
                const {
                    [opeId]: pesticidesByOperation = [],
                } = pesticidesGroupOperation
                const methodsSum = calculationMethod.filter(method => [AVERAGE_SUM, MAX_SUM].includes(method))
                if (methodsSum.length === 0) {
                    return isExceedMaxSumSubstance(pesticidesByOperation, thresholdPesticides2, listSumPesticides)
                }
                const results = methodsSum.map(method => {
                    switch (method) {
                        case AVERAGE_SUM: return isExceedAverageSumSubstance(pesticidesByOperation, thresholdPesticides2, listSumPesticides)
                        case MAX_SUM: return isExceedMaxSumSubstance(pesticidesByOperation, thresholdPesticides2, listSumPesticides)
                        default: return false
                    }
                })
                return results.some(r => r)
            }).length
            const nbExceedAveragePesticides = keys(pesticidesGroupOperation).filter(opeId => {
                const {
                    [opeId]: pesticidesByOperation = [],
                } = pesticidesGroupOperation
                const methodsSubstance = calculationMethod.filter(method => [AVERAGE_SUBSTANCE, MAX_SUBSTANCE].includes(method))
                if (methodsSubstance.length === 0) {
                    return isExceedAverageSubstance(pesticidesByOperation, thresholdPesticides1, threshold)
                }
                const results = methodsSubstance.map(method => {
                    switch (method) {
                        case AVERAGE_SUBSTANCE: return isExceedAverageSubstance(pesticidesByOperation, thresholdPesticides1, threshold)
                        case MAX_SUBSTANCE: return isExceedMaxSubstance(pesticidesByOperation, thresholdPesticides1, threshold)
                        default: return false
                    }
                })
                return results.some(r => r)
            }).length
            const nbExceedPesticides = keys(pesticidesGroupOperation).filter(opeId => {
                const {
                    [opeId]: pesticidesByOperation = [],
                } = pesticidesGroupOperation
                return isExceedPesticides(pesticidesByOperation, optionsCalculState)
            }).length

            const allData = {
                code: catchment.code,
                name: catchment.name,
                departmentNumber: catchment.department,
                department: departments.find(d => d.code === catchment.department)?.name,
                x: { value: qualitometer?.x, cellType: 'number' },
                y: { value: qualitometer?.y, cellType: 'number' },
                projection: { value: qualitometer?.projection, cellType: 'number' },
                mainPointLabel: point.mainPoint ? i18n.yes : i18n.no,
                state: i18n[getPesticidesState(pesticides, optionsCalculState)],
                pesticideThreshold: { value: thresholdPesticides2, cellType: 'number' },
                averageAnnualAverageTotalPesticides: { value: round(averageTotal, 5), format: '0.00000', cellType: 'number' },
                maxAAAByParam: { value: round(averageMax, 5), format: '0.00000', cellType: 'number' },
                annualAverageMax: { value: round(annualAverageMax, 5), format: '0.00000', cellType: 'number' },
                maxSubstance: { value: searchMaxResult(pesticides), format: '0.0000', cellType: 'number' },
                maxSumPesticides: { value: maxSumPesticides, format: '0.0000', cellType: 'number' },
                substanceNonConformityPercentage: { value: pesticides.length && nbPesticidesOperation ? (nbExceedAveragePesticides / nbPesticidesOperation) * 100 : undefined, format: '0.00', cellType: 'number' },
                sumPesticidesNonConformityPercentage: { value: pesticides.length && nbPesticidesOperation ? (nbExceedSumPesticides / nbPesticidesOperation) * 100 : undefined, format: '0.00', cellType: 'number' },
                pesticidesNonConformityPercentage: { value: pesticides.length && nbPesticidesOperation ? (nbExceedPesticides / nbPesticidesOperation) * 100 : undefined, format: '0.00', cellType: 'number' },
            }
            return showInseeCode ? allData : omit(allData, ['departmentNumber'])
        })
        const dataWithHeaders = dataContamination.length ? [
            { ...dataContamination[0], headers: keys(dataContamination[0]) },
            ...dataContamination.slice(1),
        ] : []

        dispatch(ExportAction.export(formatData(dataWithHeaders), type, i18n.evolutionContaminationNitratesPesticides))
    }

    const exportQualityReportSummaryPesticides = (type) => {
        const dataTable = catchmentPoints.map(point => {
            const catchment = catchments.find(c => c.id === point.id)
            const qualito = qualitometers.find(q => q.code === point?.codeWithoutDesignation)
            const city = qualito?.townCode && cities.find(c => c.id === qualito.townCode)

            const pesticides = qualito?.id && pesticidesAnalysisGroup[qualito.id] || []
            const quantifiedPesticides = pesticides.filter(a => a.remark === REMARK_CODES.VALID)
            const pesticidesGroupYear = groupBy(quantifiedPesticides, a => getYear(a.sampleDate))
            const pesticidesGroupParam = groupBy(quantifiedPesticides, 'parameter')

            const sumPesticides = listSumPesticides.length ? quantifiedPesticides.filter(a => listSumPesticides.includes(a.parameter)) : quantifiedPesticides
            const sumPesticidesGroupOperation = groupBy(sumPesticides, 'operation')
            const sumPesticidesGroupYear = groupBy(sumPesticides, a => getYear(a.sampleDate))

            const maxSumPesticidesByYear = rangeDate.reduce((acc, year) => {
                const {
                    [year]: sumList = [],
                } = sumPesticidesGroupYear
                const groupOperation = groupBy(sumList, 'operation')
                const listSum = keys(groupOperation).map(op => sumBy(groupOperation[op], 'result'))
                acc[`${i18n.maxSumPesticides} ${year}`] = {
                    value: max(listSum),
                    format: '0.0000',
                    cellType: 'number',
                }
                return acc
            }, {})

            const averageData = rangeDate.reduce((acc, year) => {
                const {
                    [year]: sumList = [],
                } = sumPesticidesGroupYear
                const groupOperation = groupBy(sumList, 'operation')
                const listTotalPesticides = keys(groupOperation).map(op => sumBy(groupOperation[op], 'result'))
                acc[`${i18n.averageShort} ${i18n.sum} ${i18n.pesticides} ${year}`] = {
                    value: mean(listTotalPesticides),
                    format: '0.0000',
                    cellType: 'number',
                }
                return acc
            }, {})

            const maxData = rangeDate.reduce((acc, year) => {
                acc[`${i18n.maxSubstance} ${year}`] = {
                    value: maxBy(pesticidesGroupYear[year] || [], 'result')?.result,
                    format: '0.0000',
                    cellType: 'number',
                }
                return acc
            }, {})

            const maxAnnualAverage = rangeDate.reduce((acc, year) => {
                const group = groupBy(pesticidesGroupYear[year] || [], 'parameter')
                const listAnnualAverage = keys(group).map(param => meanBy(group[param], 'result'))
                acc[`${i18n.annualAverageMax} ${year}`] = {
                    value: max(listAnnualAverage),
                    format: '0.0000',
                    cellType: 'number',
                }
                return acc
            }, {})

            const lastYearWithAnalysis = getLastYearWithAnalysis(getYear(endPeriod), sumPesticidesGroupYear, getYear(startPeriod))

            const paramGroup = groupBy(lastYearWithAnalysis, 'parameter')
            const { paramAverage, paramMax } = keys(paramGroup).reduce((acc, param) => {
                const th = thresholdsIndexed[param]?.threshold1 ?? thresholdPesticides1
                const maxParam = maxBy(paramGroup[param], 'result')?.result
                const averageParam = sumBy(paramGroup[param], 'result') / uniqBy(paramGroup[param], 'operation').length
                const label = getLabel(parameters, param, 'name')
                return {
                    paramAverage: compact([...acc.paramAverage, averageParam > th && `${label} (${round(averageParam, 4)})`]),
                    paramMax: compact([...acc.paramMax, maxParam > th && `${label} (${round(maxParam, 4)})`]),
                }
            }, { paramAverage: [], paramMax: [] })

            const nbSample = uniqBy(pesticides, 'operation').length

            const totalAAA = mean(rangeDate.filter(year => sumPesticidesGroupYear[year]?.length).map(year => {
                const pesticidesByYear = sumPesticidesGroupYear[year]
                const groupByOperation = groupBy(pesticidesByYear, 'operation')
                const listSumTotal = keys(groupByOperation).map(opeId => sumBy(groupByOperation[opeId], 'result'))
                return mean(listSumTotal)
            }))

            const nbSubstanceOverflow = quantifiedPesticides.map(a => {
                const th = thresholdsIndexed[a.parameter]?.threshold1 ?? thresholdPesticides1
                return a.result > th
            }).filter(isOverflow => isOverflow).length

            const nbSumOverflow = keys(sumPesticidesGroupOperation).map(opeId => {
                const sumTotal = sumBy(sumPesticidesGroupOperation[opeId], 'result')
                return sumTotal > thresholdPesticides2
            }).filter(isOverflow => isOverflow).length

            const maxSumPesticides = max(keys(sumPesticidesGroupOperation).map(opeId => sumBy(sumPesticidesGroupOperation[opeId], 'result')))

            const maxAAAByParam = max(keys(pesticidesGroupParam).map(param => {
                const pesticidesByParam = pesticidesGroupParam[param]
                const groupByYear = groupBy(pesticidesByParam, a => getYear(a.sampleDate))
                const listMean = keys(groupByYear).map(year => meanBy(groupByYear[year], 'result'))
                return mean(listMean)
            }))

            const maxMaxParam = maxBy(quantifiedPesticides, 'result')?.result

            const paramConcernedByMaxAAA = keys(pesticidesGroupParam).map(param => {
                const th = thresholdsIndexed[param]?.threshold1 ?? thresholdPesticides1
                const pesticidesByParam = pesticidesGroupParam[param]
                const groupByYear = groupBy(pesticidesByParam, a => getYear(a.sampleDate))
                const listMean = keys(groupByYear).map(year => meanBy(groupByYear[year], 'result'))
                const aaa = mean(listMean)
                if (aaa > th) {
                    const label = getLabel(parameters, param, 'name')
                    return `${label} (${round(aaa, 4)})`
                }
                return ''
            }).filter(l => !!l).join(', ')

            const paramConcernedByMaxMax = keys(pesticidesGroupParam).map(param => {
                const th = thresholdsIndexed[param]?.threshold1 ?? thresholdPesticides1
                const pesticidesByParam = pesticidesGroupParam[param]
                const maxByParam = maxBy(pesticidesByParam, 'result')?.result
                if (maxByParam > th) {
                    const label = getLabel(parameters, param, 'name')
                    return `${label} (${round(maxByParam, 4)})`
                }
                return ''
            }).filter(l => !!l).join(', ')

            const quantifiedParameterList = uniqBy(quantifiedPesticides, 'parameter').map(a => {
                return getLabel(parameters, a.parameter, 'name')
            }).filter(l => !!l).join(', ')

            const allData = {
                catchment: { value: catchment?.code, cellType: 'string' },
                label: catchment?.name,
                mainPointLabel: point.mainPoint ? i18n.yes : i18n.no,
                bsscode: { value: qualito?.code, cellType: 'string' },
                name: qualito?.name,
                departmentNumber: catchment?.department,
                department: departments.find(d => d.code === catchment?.department)?.name,
                city: city?.name,
                state: i18n[getPesticidesState(pesticides, optionsCalculState)],
                ...averageData,
                paramConcernedByAnnualAverageAboveThreshold: paramAverage.join(', '),
                ...maxData,
                paramConcernedByMaxAboveThreshold: paramMax.join(', '),
                ...maxAnnualAverage,
                ...maxSumPesticidesByYear,
                nbAnalysisPesticides: { value: pesticides.length, format: '0', cellType: 'number' },
                nbQuantificationsPesticides: { value: quantifiedPesticides.length, format: '0', cellType: 'number' },
                totalAAA: { value: totalAAA, format: '0.00000', cellType: 'number' },
                maxSumPesticides: { value: maxSumPesticides, format: '0.00000', cellType: 'number' },
                nbSample: { value: nbSample, format: '0', cellType: 'number' },
                nbSubstanceOverflow: { value: nbSubstanceOverflow, format: '0', cellType: 'number' },
                nbSumOverflow: { value: nbSumOverflow, format: '0', cellType: 'number' },
                maxAAAByParam: { value: maxAAAByParam, format: '0.00000', cellType: 'number' },
                paramConcernedByMaxAAA,
                maxMaxParam: { value: maxMaxParam, format: '0.00000', cellType: 'number' },
                paramConcernedByMaxMax,
                quantifiedParameterList,
            }
            return showInseeCode ? allData : omit(allData, 'departmentNumber')
        })
        const dataWithHeaders = dataTable.length ? [
            { ...dataTable[0], headers: keys(dataTable[0]) },
            ...dataTable.slice(1),
        ] : []
        dispatch(ExportAction.export(formatData(dataWithHeaders), type, i18n.qualityReportSummary))
    }

    return (
        <>
            <CardTable
                title={i18n.catchmentsQualitySummary}
                rows={data}
                headers={[
                    'code',
                    'name',
                    { key: 'stationCode', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.stationCode}</span>) },
                    { key: 'mainPointLabel', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.mainPointLabel}</span>) },
                    'city',
                    { key: 'nbAnalysisPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.nbAnalysisPesticides}</span>) },
                    { key: 'nbQuantifications', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.nbQuantifications}</span>) },
                    { key: 'pesticidesAverageMax', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.pesticidesAverageMax}</span>) },
                    { key: 'paramConcernedByAnnualAverageAboveThreshold', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.paramConcernedByAnnualAverageAboveThreshold}</span>) },
                    { key: 'maxMaxSubstance', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.maxMaxSubstance}</span>) },
                    { key: 'paramConcernedByMaxAboveThreshold', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.paramConcernedByMaxAboveThreshold}</span>) },
                    { key: 'maxSumPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.maxSumPesticides}</span>) },
                    { key: 'averageSumPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.averageSumPesticides}</span>) },
                ]}
                onClickRow={s => dispatch(push(`/station/catchment/${s.id}`))}
                rowsPerPageOptions={nbPerPageLabel}
                color={darkBlue}
                hideNbElements
                actions={[{
                    icon: 'file_download',
                    tooltip: i18n.export,
                    color: whiteColor,
                    onClick: toggleExportModal,
                }]}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={() => toggleExportModal()}
                data={[{
                    name: `${i18n.qualityReportSummary} (${i18n.pesticides})`,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => exportQualityReportSummaryPesticides('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => exportQualityReportSummaryPesticides('csv'),
                    }],
                },
                {
                    name: `${i18n.evolutionOfContaminations} (${i18n.pesticides})`,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => exportEvolutionContaminationPesticides('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => exportEvolutionContaminationPesticides('csv'),
                    }],
                }]}
            />
        </>
    )
}

ContaminationTableSummaryPesticides.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
    useP90: PropTypes.bool,
    startPeriod: PropTypes.number,
}

export { ContaminationTableSummaryNitrates, ContaminationTableSummaryPesticides }